import type { Rect } from '../../../engine/hex/types';
import type { IconRegistry } from '../../../ui/overlay/assets/IconRegistry';
import type { ModalTheme } from '../ModalTheme';
import type { HitTarget, MeasureResult, UiItem } from './Types';

type ThemeFn<T> = T | ((theme: ModalTheme) => T);

function resolve<T>(value: ThemeFn<T>, theme: ModalTheme): T {
    return typeof value === 'function' ? (value as (t: ModalTheme) => T)(theme) : value;
}

export class IconItem implements UiItem {
    private rect: Rect = { x: 0, y: 0, w: 0, h: 0 };

    constructor(
        private iconId: string,
        private size: number,
    ) {}

    measure(_ctx: CanvasRenderingContext2D, _maxW: number, _theme: ModalTheme): MeasureResult {
        return { w: this.size, h: this.size };
    }

    layout(x: number, y: number, _w: number, _theme: ModalTheme): void {
        this.rect = { x, y, w: this.size, h: this.size };
    }

    draw(ctx: CanvasRenderingContext2D, _theme: ModalTheme, icons?: IconRegistry): void {
        if (!icons) return;
        const img = icons.get(this.iconId);
        if (!img || !icons.isReady(this.iconId)) return;
        ctx.drawImage(img, this.rect.x, this.rect.y, this.rect.w, this.rect.h);
    }

    collectHitTargets(_out: HitTarget[], _scrollY: number): void {}
}

export class IconButtonItem implements UiItem {
    private rect: Rect = { x: 0, y: 0, w: 0, h: 0 };

    constructor(
        private actionId: string,
        private iconId: string,
        private size: number,
        private enabled: boolean = true,
    ) {}

    measure(_ctx: CanvasRenderingContext2D, _maxW: number, _theme: ModalTheme): MeasureResult {
        return { w: this.size, h: this.size };
    }

    layout(x: number, y: number, _w: number, _theme: ModalTheme): void {
        this.rect = { x, y, w: this.size, h: this.size };
    }

    draw(ctx: CanvasRenderingContext2D, _theme: ModalTheme, icons?: IconRegistry): void {
        if (!icons) return;
        const img = icons.get(this.iconId);
        if (!img || !icons.isReady(this.iconId)) return;
        ctx.drawImage(img, this.rect.x, this.rect.y, this.rect.w, this.rect.h);
    }

    collectHitTargets(out: HitTarget[], scrollY: number): void {
        out.push({
            id: this.actionId,
            enabled: this.enabled,
            rect: { x: this.rect.x, y: this.rect.y - scrollY, w: this.rect.w, h: this.rect.h },
        });
    }
}

export class ThemedIconItem implements UiItem {
    private rect: Rect = { x: 0, y: 0, w: 0, h: 0 };
    private size = 0;

    constructor(
        private iconId: string,
        private sizeFn: ThemeFn<number>,
    ) {}

    measure(_ctx: CanvasRenderingContext2D, _maxW: number, theme: ModalTheme): MeasureResult {
        this.size = resolve(this.sizeFn, theme);
        return { w: this.size, h: this.size };
    }

    layout(x: number, y: number, _w: number, _theme: ModalTheme): void {
        this.rect = { x, y, w: this.size, h: this.size };
    }

    draw(ctx: CanvasRenderingContext2D, _theme: ModalTheme, icons?: IconRegistry): void {
        if (!icons) return;
        const img = icons.get(this.iconId);
        if (!img || !icons.isReady(this.iconId)) return;
        ctx.drawImage(img, this.rect.x, this.rect.y, this.rect.w, this.rect.h);
    }

    collectHitTargets(_out: HitTarget[], _scrollY: number): void {}
}

export class ThemedIconButtonItem implements UiItem {
    private rect: Rect = { x: 0, y: 0, w: 0, h: 0 };
    private size = 0;

    constructor(
        private actionId: string,
        private iconId: string,
        private sizeFn: ThemeFn<number>,
        private enabled: boolean = true,
    ) {}

    measure(_ctx: CanvasRenderingContext2D, _maxW: number, theme: ModalTheme): MeasureResult {
        this.size = resolve(this.sizeFn, theme);
        return { w: this.size, h: this.size };
    }

    layout(x: number, y: number, _w: number, _theme: ModalTheme): void {
        this.rect = { x, y, w: this.size, h: this.size };
    }

    draw(ctx: CanvasRenderingContext2D, _theme: ModalTheme, icons?: IconRegistry): void {
        if (!icons) return;
        const img = icons.get(this.iconId);
        if (!img || !icons.isReady(this.iconId)) return;
        ctx.drawImage(img, this.rect.x, this.rect.y, this.rect.w, this.rect.h);
    }

    collectHitTargets(out: HitTarget[], scrollY: number): void {
        out.push({
            id: this.actionId,
            enabled: this.enabled,
            rect: { x: this.rect.x, y: this.rect.y - scrollY, w: this.rect.w, h: this.rect.h },
        });
    }
}
